import React from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import styles from './web-footer-content.module.css';
import { routes } from '../../../../config/routes';
import { showCookiePreferences } from '../../../../hooks/use-cookies';
import { Logo } from '../../../../components/app/logo/logo';
import { Social } from '../../../../components/web/social/social';
import { IconCookies } from '../../../../components/app/icons/cookies';
import { Atreo } from '../../../../components/app/atreo/atreo';

const FooterLinks: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <nav aria-label={title}>
        <h3 className="mb-[2rem] text-[1.6rem] font-bold text-brand-secondary leading-[1.3]">{title}</h3>
        <div className="flex flex-col items-start gap-[1.2rem] text-[1.5rem] leading-relaxed text-web-copy [&_a]:transition-colors [&_a:hover]:text-brand-primary [&_a:focus-visible]:outline-brand-primary">
            {children}
        </div>
    </nav>
);

export const WebFooterContent: React.FC = () => {
    const { t } = useTranslation();
    const { pathname } = useRouter();
    return (
        <>
            <div className="flex w-full flex-col items-center justify-center gap-small text-[1.4rem] leading-[1.4rem] xs:flex-row xs:justify-between">
                <div className="[&_svg_path]:fill-web-copy [&_svg_rect]:fill-web-copy [&_svg>path:nth-of-type(-n+2)]:fill-[#51463f]">
                    <Logo href={routes.web.index} inverse />
                </div>
                <div className="flex items-center gap-xsmall">
                    <Social neutral type="Facebook" link="https://www.facebook.com/monery.io" />
                    <Social neutral type="Instagram" link="https://www.instagram.com/monery.io" />
                    <Social neutral type="Discord" link="https://discord.gg/n4MYgUh9KF" />
                </div>
            </div>
            <div
                className={`${styles.dividers} my-[4rem] grid grid-cols-1 gap-[3.6rem] py-[4rem] sm:grid-cols-2 lg:my-[4.8rem] lg:grid-cols-[1fr_1fr_1.4fr] lg:gap-[6rem]`}
            >
                <FooterLinks title="Navigace">
                    <a href={routes.web.pricing}>Ceník</a>
                    <a href={routes.web.compoundInterest}>Kalkulačka složeného úročení</a>
                    <a href={routes.web.contact}>Kontakty</a>
                </FooterLinks>
                <FooterLinks title="Podpora">
                    <a href={routes.web.faq}>Často kladené otázky</a>
                    <a
                        href={`https://monery.zendesk.com/hc/cs/sections/29109217807377-Export-dat`}
                        target={'_blank'}
                        rel="noreferrer"
                    >
                        Návody na export dat
                    </a>
                </FooterLinks>
                <FooterLinks title={t('pages.layout.footer.legal')}>
                    {pathname === routes.web.affiliate && (
                        <a href={routes.web.affiliateTerms}>{t('pages.affiliateLanding.termsTitle')}</a>
                    )}
                    <Link href={routes.web.termsAndConditions}>
                        <a>{t('pages.layout.footer.termsAndConditions')}</a>
                    </Link>
                    <Link href={routes.web.privacyPolicy}>
                        <a>{t('pages.layout.footer.privacyPolicy')}</a>
                    </Link>
                    <Link href={routes.web.marketingCommunicationTerms}>
                        <a>{t('pages.layout.footer.marketingCommunicationTerms')}</a>
                    </Link>
                </FooterLinks>
            </div>

            <div className="flex w-full flex-col items-center justify-center gap-small text-[1.4rem] leading-[1.4rem] xs:flex-row xs:justify-between">
                <div className="flex flex-col gap-xsmall">
                    <div className="flex items-center gap-xxsmall text-web-copy max-[768px]:w-full max-[768px]:text-center [&_a]:text-web-copy [&_a]:underline [&_a:hover]:text-brand-primary">
                        &copy; Monery Finance s.r.o &mdash;{' '}
                        <button
                            className="inline-flex h-[2.6rem] w-[2.6rem] cursor-pointer items-center justify-center rounded-full border border-[#d9cdc5] text-web-copy transition-all [&_i]:h-[1.4rem] [&_i]:w-[1.4rem] hover:bg-[#d9cdc5]/40"
                            aria-label="Nastavení cookies"
                            onClick={showCookiePreferences}
                            type="button"
                        >
                            <IconCookies />
                        </button>
                    </div>
                </div>

                <div className="[&_svg_path]:!fill-web-copy">
                    <Atreo />
                </div>
            </div>
        </>
    );
};
